import { useState } from 'react';
import Modal from '../components/Modal.jsx';
import { useConfirm } from '../components/ConfirmDialog.jsx';
import { fmtCents, parseAmountToCents } from '../format.js';

export default function SinkingFunds({ state, generic, showToast, onBack }) {
  const [editing, setEditing] = useState(null);
  const visible = state.sinking_funds.filter(s => !s.archived).sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  const totalAnnual = visible.reduce((s, x) => s + x.annual_cents, 0);
  const totalMonthly = visible.reduce((s, x) => s + x.monthly_accrual_cents, 0);

  return (
    <div className="screen">
      <div className="row" style={{ marginBottom: 16, gap: 12 }}>
        <button onClick={onBack} className="ghost">‹</button>
        <h1 style={{ margin: 0 }}>Sinking Funds</h1>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="row between">
          <div>
            <div className="muted tiny">Annual</div>
            <div className="amount" style={{ fontWeight: 600 }}>{fmtCents(totalAnnual)}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="muted tiny">Monthly accrual</div>
            <div className="amount" style={{ fontWeight: 600 }}>{fmtCents(totalMonthly)}</div>
          </div>
        </div>
      </div>

      <div className="row between" style={{ marginBottom: 12 }}>
        <span className="muted tiny">{visible.length} buckets</span>
        <button className="primary" onClick={() => setEditing('new')}>+ New</button>
      </div>

      <div className="card" style={{ padding: 0 }}>
        {visible.length === 0 ? (
          <div className="empty"><div className="icon">∅</div><div>No sinking funds</div></div>
        ) : visible.map(s => (
          <button key={s.id} onClick={() => setEditing(s)} className="list-item"
                  style={{ width: '100%', textAlign: 'left', background: 'transparent', border: 'none',
                           borderBottom: '1px solid var(--border2)', borderRadius: 0, cursor: 'pointer' }}>
            <div className="grow">
              <div>{s.item}</div>
              <div className="meta">
                {fmtCents(s.monthly_accrual_cents)}/mo · drains {s.drain_months || '—'}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div className="amount">{fmtCents(s.annual_cents)}/yr</div>
              {s.current_cents > 0 && <div className="tiny muted">bal {fmtCents(s.current_cents)}</div>}
            </div>
          </button>
        ))}
      </div>

      <SinkingEditor editing={editing} onClose={() => setEditing(null)}
                     generic={generic} showToast={showToast} />
    </div>
  );
}

function SinkingEditor({ editing, onClose, generic, showToast }) {
  if (!editing) return null;
  const isNew = editing === 'new';
  const initial = isNew ? {
    item: '', annual_cents: 0, monthly_accrual_cents: 0,
    drain_months: '', current_cents: 0, notes: '',
  } : editing;

  return (
    <Modal open={!!editing} onClose={onClose} title={isNew ? 'New Sinking Fund' : 'Edit Sinking Fund'}>
      <SinkingForm key={isNew ? 'new' : editing.id} initial={initial} isNew={isNew}
                   generic={generic} showToast={showToast} onClose={onClose} />
    </Modal>
  );
}

function SinkingForm({ initial, isNew, generic, showToast, onClose }) {
  const ask = useConfirm();
  const [form, setForm] = useState({
    item: initial.item ?? '',
    annualInput: initial.annual_cents ? (initial.annual_cents / 100).toFixed(2) : '',
    monthlyInput: initial.monthly_accrual_cents ? (initial.monthly_accrual_cents / 100).toFixed(2) : '',
    drain_months: initial.drain_months ?? '',
    currentInput: initial.current_cents ? (initial.current_cents / 100).toFixed(2) : '',
    notes: initial.notes ?? '',
    autoCompute: !initial.monthly_accrual_cents && !!initial.annual_cents,
  });
  const [saving, setSaving] = useState(false);
  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Auto-compute monthly if user wants
  function syncMonthly(annualStr) {
    if (!form.autoCompute) return;
    const c = parseAmountToCents(annualStr);
    if (c != null) update('monthlyInput', (c / 1200).toFixed(2));
  }

  async function save() {
    if (!form.item) return showToast({ kind: 'error', message: 'Item required.' });
    setSaving(true);
    try {
      const payload = {
        item: form.item.trim(),
        annual_cents: parseAmountToCents(form.annualInput) ?? 0,
        monthly_accrual_cents: parseAmountToCents(form.monthlyInput) ?? 0,
        drain_months: form.drain_months,
        current_cents: parseAmountToCents(form.currentInput) ?? 0,
        notes: form.notes,
      };
      if (isNew) await generic.sinking_funds.create(payload);
      else await generic.sinking_funds.update(initial.id, payload);
      showToast({ kind: 'success', message: 'Saved.' });
      onClose();
    } catch (e) {
      showToast({ kind: 'error', message: 'Failed: ' + e.message });
    } finally { setSaving(false); }
  }

  async function archive() {
    const ok = await ask({ title: 'Archive sinking fund?', message: 'You can restore it later.', confirmLabel: 'Archive', danger: true }); if (!ok) return;
    setSaving(true);
    try {
      await generic.sinking_funds.remove(initial.id);
      showToast({ kind: 'success', message: 'Archived.' });
      onClose();
    } catch (e) {
      showToast({ kind: 'error', message: 'Failed: ' + e.message });
    } finally { setSaving(false); }
  }

  return (
    <>
      <div className="field"><label>Item</label>
        <input value={form.item} onChange={(e) => update('item', e.target.value)} autoFocus /></div>
      <div className="row" style={{ gap: 8 }}>
        <div className="field" style={{ flex: 1 }}><label>Annual cost</label>
          <input inputMode="decimal" value={form.annualInput}
                 onChange={(e) => { update('annualInput', e.target.value); syncMonthly(e.target.value); }} /></div>
        <div className="field" style={{ flex: 1 }}><label>Monthly accrual</label>
          <input inputMode="decimal" value={form.monthlyInput}
                 onChange={(e) => { update('monthlyInput', e.target.value); update('autoCompute', false); }} /></div>
      </div>
      <label className="tiny muted" style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
        <input type="checkbox" checked={form.autoCompute}
               onChange={(e) => { update('autoCompute', e.target.checked); if (e.target.checked) syncMonthly(form.annualInput); }}
               style={{ width: 'auto' }} />
        Auto-compute monthly = annual ÷ 12
      </label>
      <div className="field"><label>Drain Months</label>
        <input value={form.drain_months} onChange={(e) => update('drain_months', e.target.value)}
               placeholder="e.g. February, or May + November" /></div>
      <div className="field"><label>Current Balance</label>
        <input inputMode="decimal" value={form.currentInput} onChange={(e) => update('currentInput', e.target.value)} /></div>
      <div className="field"><label>Notes</label>
        <input value={form.notes} onChange={(e) => update('notes', e.target.value)} /></div>
      <div className="row between" style={{ marginTop: 24 }}>
        {!isNew ? <button className="danger" onClick={archive} disabled={saving}>Archive</button> : <span />}
        <div className="row" style={{ gap: 8 }}>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" onClick={save} disabled={saving}>{saving ? <span className="spinner"/> : 'Save'}</button>
        </div>
      </div>
    </>
  );
}
